// ======================================================================
// FILE: src/modules/wms/dashboard/pages/WMSDashboardPage.jsx
// MODULE: Work Management System (WMS)
// FEATURE: Dashboard
//
// PURPOSE:
//   Dashboard Landing Page
//
// RESPONSIBILITIES:
//   - Load dashboard data from backend
//   - Maintain dashboard filters
//   - Load Team / Executive filter masters
//   - Pass dashboard data to existing dashboard components
//   - Trigger dashboard refresh on Search / Reset
//
// ARCHITECTURE:
//
//   WMSDashboardPage
//        ↓
//   wmsDashboardService
//        ↓
//   GET /api/wms/dashboard
//
//   Existing dashboard components are retained.
// ======================================================================

import React, {
  useCallback,
  useEffect,
  useState,
} from "react";


// ======================================================================
// EXISTING DASHBOARD COMPONENTS
// ======================================================================

import DashboardHeader
  from "../components/DashboardHeader";

import DashboardFilterBar
  from "../components/DashboardFilterBar";

import DashboardKpiGrid
  from "../components/DashboardKpiGrid";

import MyWorkQueueCard
  from "../components/MyWorkQueueCard";

import TodaysCompletionCard
  from "../components/TodaysCompletionCard";

import WorkTrendCard
  from "../components/WorkTrendCard";

import StatusDistributionCard
  from "../components/StatusDistributionCard";

import PriorityDistributionCard
  from "../components/PriorityDistributionCard";

import TeamPerformanceTable
  from "../components/TeamPerformanceTable";

import ExecutivePerformanceTable
  from "../components/ExecutivePerformanceTable";

import SLAComplianceCard
  from "../components/SLAComplianceCard";

import PendingApprovalsCard
  from "../components/PendingApprovalsCard";

import UpcomingScheduleCard
  from "../components/UpcomingScheduleCard";


// ======================================================================
// SERVICES
// ======================================================================

import wmsDashboardService
  from "../services/wmsDashboardService";

import wmsTeamService
  from "../../services/wmsTeamService";


// ======================================================================
// DEFAULT FILTERS
// ======================================================================

// ======================================================================
// DEFAULT DATE RANGE
// ======================================================================

const getDefaultDateRange = () => {

  const today = new Date();

  const from = new Date(
    today
  );

  // Last 7 calendar days including today
  from.setDate(
    today.getDate() - 6
  );

  const formatDate = (date) => {

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");

    const day =
      String(
        date.getDate()
      ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  return {
    date_from: formatDate(from),
    date_to: formatDate(today),
  };
};


const DEFAULT_DATE_RANGE =
  getDefaultDateRange();


// ======================================================================
// DEFAULT FILTERS
// ======================================================================

const DEFAULT_FILTERS = {

  date_from:
    DEFAULT_DATE_RANGE.date_from,

  date_to:
    DEFAULT_DATE_RANGE.date_to,

  team_id: "",

  executive_id: "",

  priority: "",

  status: "",

  work_mode: "",

  search: "",
};


// ======================================================================
// PAGE
// ======================================================================

export default function WMSDashboardPage() {


  // ====================================================================
  // DASHBOARD DATA
  // ====================================================================

  const [
    dashboard,
    setDashboard
  ] = useState({});


  // ====================================================================
  // LOADING
  // ====================================================================

  const [
    loading,
    setLoading
  ] = useState(true);


  // ====================================================================
  // ERROR
  // ====================================================================

  const [
    error,
    setError
  ] = useState("");


  // ====================================================================
  // FILTER STATE
  // ====================================================================

  const [
    filters,
    setFilters
  ] = useState({
    ...DEFAULT_FILTERS
  });


  // ====================================================================
  // TEAM FILTER OPTIONS
  // ====================================================================

  const [
    teams,
    setTeams
  ] = useState([]);


  // ====================================================================
  // EXECUTIVE FILTER OPTIONS
  // ====================================================================

  const [
    executives,
    setExecutives
  ] = useState([]);


  // ====================================================================
  // LOAD DASHBOARD
  //
  // IMPORTANT:
  // This callback intentionally does NOT depend on `filters`.
  //
  // Filter changes should only update the UI.
  // The backend should be called when Search is clicked.
  // ====================================================================

  const loadDashboard = useCallback(
    async (activeFilters = DEFAULT_FILTERS) => {

      try {

        setLoading(true);

        setError("");


        const data =
          await wmsDashboardService.getDashboard(
            activeFilters
          );


        setDashboard(
          data || {}
        );


      } catch (err) {

        console.error(
          "WMS Dashboard load failed:",
          err
        );


        setError(
          err?.response?.data?.message
          ||
          err?.message
          ||
          "Failed to load WMS Dashboard."
        );


      } finally {

        setLoading(false);

      }

    },
    []
  );


  // ====================================================================
  // LOAD TEAM / EXECUTIVE FILTER MASTERS
  // ====================================================================

  const loadFilterMasters = useCallback(
    async () => {

      try {


        // --------------------------------------------------------------
        // Teams
        // --------------------------------------------------------------

        const teamResult =
          await wmsTeamService.listTeams();


        setTeams(
          teamResult?.items || []
        );


        // --------------------------------------------------------------
        // Executives / Team Members
        // --------------------------------------------------------------

        const executiveResult =
          await wmsTeamService.getAllMembers();


        setExecutives(
          executiveResult || []
        );


      } catch (err) {

        console.error(
          "WMS Dashboard filter master load failed:",
          err
        );


        // --------------------------------------------------------------
        // Filter master failure should not prevent
        // dashboard itself from loading.
        // --------------------------------------------------------------

        setTeams([]);

        setExecutives([]);

      }

    },
    []
  );


  // ====================================================================
  // INITIAL LOAD
  // ====================================================================

  useEffect(() => {

    loadFilterMasters();

    loadDashboard({
      ...DEFAULT_FILTERS
    });

  }, [
    loadFilterMasters,
    loadDashboard
  ]);


  // ====================================================================
  // FILTER CHANGE
  // ====================================================================

  const handleFilterChange = (
    name,
    value
  ) => {

    setFilters(
      previous => ({
        ...previous,

        [name]: value,

      })
    );

  };


  // ====================================================================
  // SEARCH / APPLY FILTERS
  // ====================================================================

  const handleSearch = () => {

    loadDashboard({
      ...filters
    });

  };


  // ====================================================================
  // RESET FILTERS
  // ====================================================================

  const handleReset = () => {

    const resetFilters = {
      ...DEFAULT_FILTERS
    };


    setFilters(
      resetFilters
    );


    loadDashboard(
      resetFilters
    );

  };


  // ====================================================================
  // PAGE
  // ====================================================================

  return (

    <div className="container-fluid py-4">


      {/* ================================================================
          DASHBOARD HEADER
          ================================================================ */}

      <DashboardHeader
            onRefresh={() => {
              loadDashboard({
                ...filters
              });
            }}
            loading={loading}
          />


      {/* ================================================================
          ERROR
          ================================================================ */}

      {error && (

        <div
          className="alert alert-danger"
          role="alert"
        >

          {error}

        </div>

      )}


      {/* ================================================================
          FILTER BAR
          ================================================================ */}

      <DashboardFilterBar

        filters={
          filters
        }

        teams={
          teams
        }

        executives={
          executives
        }

        onFilterChange={
          handleFilterChange
        }

        onSearch={
          handleSearch
        }

        onReset={
          handleReset
        }

        loading={
          loading
        }

      />


      {/* ================================================================
          LOADING INDICATOR
          ================================================================ */}

      {loading && (

        <div className="text-center py-3">

          <div
            className="spinner-border text-primary"
            role="status"
          />

          <div className="mt-2 text-muted">

            Loading WMS Dashboard...

          </div>

        </div>

      )}


      {/* ================================================================
          KPI GRID
          ================================================================ */}

      <DashboardKpiGrid

        data={
          dashboard?.kpis
          || {}
        }

      />


      {/* ================================================================
          MY WORK QUEUE + TODAY'S COMPLETION
          ================================================================ */}

      <div className="row g-3 mb-4">


        <div className="col-12 col-lg-6">

          <MyWorkQueueCard
              data={dashboard?.my_work_queue || []}
            />

        </div>


        <div className="col-12 col-lg-6">

          <TodaysCompletionCard

            data={
              dashboard?.today_completion
              || {}
            }

          />

        </div>


      </div>


      {/* ================================================================
          WORK TREND
          ================================================================ */}

      <WorkTrendCard

        data={
          dashboard?.work_trend
          || []
        }

      />


      {/* ================================================================
          STATUS + PRIORITY DISTRIBUTION
          ================================================================ */}

      <div className="row g-3 mb-4">


        <div className="col-12 col-lg-6">

          <StatusDistributionCard

            data={
              dashboard?.status_distribution
              || []
            }

          />

        </div>


        <div className="col-12 col-lg-6">

          <PriorityDistributionCard

            data={
              dashboard?.priority_distribution
              || []
            }

          />

        </div>


      </div>


      {/* ================================================================
          TEAM + EXECUTIVE PERFORMANCE
          ================================================================ */}

      <div className="row g-3 mb-4">


        <div className="col-12 col-xl-6">

          <TeamPerformanceTable

            data={
              dashboard?.team_performance
              || []
            }

          />

        </div>


        <div className="col-12 col-xl-6">

          <ExecutivePerformanceTable

            data={
              dashboard?.executive_performance
              || []
            }

          />

        </div>


      </div>


      {/* ================================================================
          SLA COMPLIANCE
          ================================================================ */}

      <SLAComplianceCard

        data={
          dashboard?.sla
          || {}
        }

      />


      {/* ================================================================
          PENDING APPROVALS + UPCOMING SCHEDULE
          ================================================================ */}

      <div className="row g-3 mb-4">


        <div className="col-12 col-xl-6">

          <PendingApprovalsCard

            data={
              dashboard?.pending_approvals
              || []
            }

          />

        </div>


        <div className="col-12 col-xl-6">

          <UpcomingScheduleCard

            data={
              dashboard?.upcoming_schedule
              || []
            }

          />

        </div>


      </div>


    </div>

  );

}